import React, { useMemo, useState } from "react";
import {
    Popover,
    IconButton,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useTranslation } from "react-i18next";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useTheme } from "@mui/material/styles";
import { useAppSelector } from "../../framework/customStore";
import { selectColorSchemeByScope } from "../../store/colorSchemeSlice";
import { shiftColors, ColorCriterion } from "./utils/ShiftColors";

interface InstructionItemProps {
  text: string;
}

const InstructionItem = ({ text }: InstructionItemProps) => (
    <ListItem disableGutters>
        <ListItemIcon sx={{ minWidth: 24 }}>
            <FiberManualRecordIcon sx={{ fontSize: "0.75rem", color: "text.secondary" }} />
        </ListItemIcon>
        <ListItemText
            primary={text}
            primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
        />
    </ListItem>
);

export default function WorkShiftHelpPopover() {
    const { t } = useTranslation();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const colorScheme = useAppSelector(selectColorSchemeByScope("workShiftPlanning"));
    const isColorCodingOn = !!colorScheme?.enabled && !!colorScheme?.mappings && Object.keys(colorScheme.mappings).length > 0;

    const legend = useMemo(() => {
        if (!isColorCodingOn) {
            return {
                gray: { labelKey: "workShiftPlanning.instructions.colorGray", color: "#e0e0e0" },
                light: { labelKey: "workShiftPlanning.instructions.colorYellow", color: theme.palette.warning.light },
                dark: { labelKey: "workShiftPlanning.instructions.colorGreen", color: theme.palette.green?.extraLight || "#4caf50" },
            };
        }
        const defaultKey = colorScheme?.defaultColorKey ?? "blue";
        const palette = shiftColors[defaultKey];
        const grayLabelKey = colorScheme?.criterion === ColorCriterion.Route
            ? "workShiftPlanning.instructions.missingRoute"
            : colorScheme?.criterion === ColorCriterion.Vehicle
                ? "workShiftPlanning.instructions.missingVehicle"
                : "workShiftPlanning.instructions.missingEmployee";
        return {
            gray: { labelKey: grayLabelKey, color: "#e0e0e0" },
            light: { labelKey: "workShiftPlanning.instructions.colorLight", color: palette.light },
            dark: { labelKey: "workShiftPlanning.instructions.colorDark", color: palette.dark },
        };
    }, [isColorCodingOn, colorScheme?.defaultColorKey, colorScheme?.criterion, theme.palette]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "workshift-help-popover" : undefined;

    return (
        <Grid2 xs="auto">
            <IconButton
                color="primary"
                aria-describedby={id}
                onClick={handleOpen}
                size="small"
                sx={{ ml: 1 }}
            >
                <InfoIcon />
            </IconButton>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left"
                }}
            >
                <Box p={2} maxWidth={360}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                        {t("workShiftPlanning.instructions.colorLegendTitle")}
                    </Typography>
                    <List dense disablePadding>
                        <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                                <FiberManualRecordIcon sx={{ color: legend.gray.color }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t(legend.gray.labelKey)}
                                primaryTypographyProps={{ variant: "body2" }}
                            />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                                <FiberManualRecordIcon sx={{ color: legend.light.color }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t(legend.light.labelKey)}
                                primaryTypographyProps={{ variant: "body2" }}
                            />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                                <FiberManualRecordIcon sx={{ color: legend.dark.color }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t(legend.dark.labelKey)}
                                primaryTypographyProps={{ variant: "body2" }}
                            />
                        </ListItem>
                    </List>
                    <Box mt={1} display="flex" alignItems="center" gap={0.5} color="text.secondary">
                        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption" color="inherit" fontSize={14}>
                            {t("workShiftPlanning.tooltips.publishOnlyWithEmployee")}
                        </Typography>
                    </Box>
                    <Typography mt={2} variant="subtitle2" color="primary.main" gutterBottom>
                        {t("workShiftPlanning.instructions.title")}
                    </Typography>
                    <List dense disablePadding>
                        <InstructionItem text={t("workShiftPlanning.instructions.copy")} />
                        <InstructionItem text={t("workShiftPlanning.instructions.cut")} />
                        <InstructionItem text={t("workShiftPlanning.instructions.paste")} />
                        <InstructionItem text={t("workShiftPlanning.instructions.multiSelectCtrl")} />
                        <InstructionItem text={t("workShiftPlanning.instructions.multiSelectRange")} />
                    </List>
                </Box>
            </Popover>
        </Grid2>
    );
}
